import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EmailIcon from "@mui/icons-material/Email";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import JobForm from "../../components/service/jobForm.component";
import { SettlementCreator } from "../../components/settlements/settlementCreator/settlementCreator.component";
import axios from "../../api/axios";
import {
  EMAIL_SERVICE_STATUS_ENDPOINT,
  ERROR_SNACKBAR,
  INFO_SNACKBAR,
  MAIL_INGESTION_RUN_ENDPOINT,
  ONLINE_USERS_ENDPOINT,
} from "../../consts";
import { useHasAnyPermission, useHasPermission } from "../../contexts/auth";
import { MailIngestionLogs } from "./mailIngestionLogs.component";
import { OnlineUser, OnlineUsersList } from "./onlineUsersList.component";

type EmailServiceStatusResponse = {
  is_healthy: boolean;
  error: string | null;
};

type OnlineUsersResponse = {
  count: number;
  results: OnlineUser[];
};

export const DashboardActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [logsOpened, setLogsOpened] = useState<boolean>(false);
  const [onlineUsersOpened, setOnlineUsersOpened] = useState<boolean>(false);
  const [addJobOpened, setAddJobOpened] = useState<boolean>(false);
  const [generateSettlementOpened, setGenerateSettlementOpened] = useState<boolean>(false);
  const [isEmailServiceHealthy, setIsEmailServiceHealthy] = useState<boolean | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [onlineUsersCount, setOnlineUsersCount] = useState<number | null>(null);
  const [onlineUsersLoading, setOnlineUsersLoading] = useState(false);
  const [onlineUsersError, setOnlineUsersError] = useState<string | null>(null);
  const [isStartingIngestion, setIsStartingIngestion] = useState(false);
  const [ingestionLogsRefreshKey, setIngestionLogsRefreshKey] = useState(0);
  const canRunMailIngestion = useHasPermission("settlement.view_mailingestionlog");
  const canViewOnlineUsers = useHasPermission("users.view_online_users");
  const canAddJob = useHasAnyPermission(["service.add_job", "service.add_internal_job"]);
  const canAddSettlement = useHasPermission("settlement.add_settlement");

  useEffect(() => {
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const response = await axios.get<EmailServiceStatusResponse>(EMAIL_SERVICE_STATUS_ENDPOINT);
        if (isMounted) {
          setIsEmailServiceHealthy(Boolean(response.data?.is_healthy));
        }
      } catch {
        if (isMounted) {
          setIsEmailServiceHealthy(false);
        }
      }
    };

    fetchStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchOnlineUsers = async (showErrorNotification = true) => {
    if (!canViewOnlineUsers) {
      return;
    }

    setOnlineUsersLoading(true);
    setOnlineUsersError(null);
    try {
      const response = await axios.get<OnlineUsersResponse>(ONLINE_USERS_ENDPOINT);
      setOnlineUsers(response.data?.results ?? []);
      setOnlineUsersCount(response.data?.count ?? 0);
    } catch {
      setOnlineUsers([]);
      setOnlineUsersCount(0);
      const errorMessage = t("Failed to fetch online users");
      setOnlineUsersError(errorMessage);
      if (showErrorNotification) {
        enqueueSnackbar(errorMessage, ERROR_SNACKBAR);
      }
    } finally {
      setOnlineUsersLoading(false);
    }
  };

  useEffect(() => {
    void fetchOnlineUsers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewOnlineUsers]);

  useEffect(() => {
    if (!onlineUsersOpened) {
      return;
    }

    void fetchOnlineUsers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineUsersOpened]);

  const statusColor =
    isEmailServiceHealthy === true
      ? "#2ecc71"
      : isEmailServiceHealthy === false
        ? "#e74c3c"
        : "#95a5a6";

  const statusIcon = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <EmailIcon sx={{ fontSize: 18 }} />
    </Box>
  );

  const statusLight = (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: statusColor,
        boxShadow: `0 0 0 1px rgba(0, 0, 0, 0.12), 0 0 8px ${statusColor}66`,
        flexShrink: 0,
      }}
    />
  );

  const onlineUsersCountAdornment = (
    <Box
      sx={{
        minWidth: 10,
        height: 22,
        borderRadius: 999,
        px: 0.75,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        bgcolor: "rgba(25, 118, 210, 0.12)",
        color: "primary.main",
      }}
    >
      {onlineUsersCountLoadingLabel(onlineUsersCount, onlineUsersLoading)}
    </Box>
  );

  function onlineUsersCountLoadingLabel(count: number | null, loading: boolean) {
    if (loading && count === null) {
      return "...";
    }
    return count ?? 0;
  }

  const actions: Action[] = [
    {
      id: "new-job",
      icon: <AddIcon />,
      label: t("New job"),
      onClick: () => setAddJobOpened(true),
      disabled: !canAddJob,
    },
    {
      id: "settlement-creator",
      icon: <RequestQuoteIcon />,
      label: t("Settlement Creator"),
      onClick: () => setGenerateSettlementOpened(true),
      disabled: !canAddSettlement,
    },
    {
      icon: statusIcon,
      label: t("Email service status"),
      trailingAdornment: statusLight,
      onClick: () => setLogsOpened(true),
    },
    ...(canViewOnlineUsers
      ? [
          {
            icon: <PeopleAltIcon />,
            label: t("Online users"),
            trailingAdornment: onlineUsersCountAdornment,
            onClick: () => setOnlineUsersOpened(true),
          },
        ]
      : []),
  ];

  const handleRunIngestion = async () => {
    if (isStartingIngestion) {
      return;
    }

    setIsStartingIngestion(true);
    try {
      // The backend only queues a Celery task here, so this request returns
      // immediately; the ingestion itself (IMAP fetch + OCR) runs in the background.
      const response = await axios.post(MAIL_INGESTION_RUN_ENDPOINT);
      enqueueSnackbar(
        response.data?.detail ||
          t(
            "Counters ingestion has started. It runs in the background — refresh the history in a moment to see the result."
          ),
        INFO_SNACKBAR
      );
    } catch (error) {
      const status = (error as any)?.response?.status;
      const backendDetail =
        (error as any)?.response?.data?.detail ||
        (error as any)?.response?.data?.message ||
        "";

      // 429: the per-tenant cooldown between manual runs is still active — this
      // isn't an error, just tell the user to wait.
      if (status === 429 && backendDetail) {
        enqueueSnackbar(backendDetail, INFO_SNACKBAR);
      } else {
        enqueueSnackbar(
          backendDetail || t("Failed to start counters ingestion"),
          ERROR_SNACKBAR
        );
      }
    } finally {
      setIsStartingIngestion(false);
    }
  };

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={addJobOpened}
        setOpened={setAddJobOpened}
        title={t("New job")}
        content={
          <JobForm
            onSuccess={() => {
              setAddJobOpened(false);
              navigate("/service/jobs");
            }}
          />
        }
      />
      <CustomModal
        opened={generateSettlementOpened}
        setOpened={setGenerateSettlementOpened}
        title={t("Settlement Creator")}
        content={
          <SettlementCreator
            onSuccess={() => setGenerateSettlementOpened(false)}
          />
        }
        width="min(1040px, 94vw)"
      />
      <CustomModal
        opened={logsOpened}
        setOpened={setLogsOpened}
        title={t("History of counters ingestion")}
        headerActions={
          <Stack direction="row" spacing={1} alignItems="center">
            {canRunMailIngestion && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => void handleRunIngestion()}
                disabled={isStartingIngestion}
              >
                {isStartingIngestion ? t("Starting ingestion...") : t("Run ingestion")}
              </Button>
            )}
            <Tooltip title={t("Refresh history")}>
              <IconButton
                size="small"
                onClick={() => setIngestionLogsRefreshKey((key) => key + 1)}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        content={<MailIngestionLogs refreshKey={ingestionLogsRefreshKey} />}
        width={600}
      />
      <CustomModal
        opened={onlineUsersOpened}
        setOpened={setOnlineUsersOpened}
        title={t("Online users")}
        content={
          <OnlineUsersList
            users={onlineUsers}
            loading={onlineUsersLoading}
            error={onlineUsersError}
          />
        }
        width={600}
      />
    </>
  );
};
